import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, MapPin, Ruler, Compass, Home, Eye, Share2, Check, Copy, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import VillaRenderView from '@/components/Villa3D';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = window.location.origin;

const facingMap = {
  en: { EAST: 'East', WEST: 'West', NORTH: 'North', SOUTH: 'South', 'NORTH & EAST': 'North-East', 'NORTH & WEST': 'North-West', 'SOUTH & EAST': 'South-East' },
  ta: { EAST: 'கிழக்கு', WEST: 'மேற்கு', NORTH: 'வடக்கு', SOUTH: 'தெற்கு', 'NORTH & EAST': 'வடகிழக்கு', 'NORTH & WEST': 'வடமேற்கு', 'SOUTH & EAST': 'தென்கிழக்கு' },
};

export default function PlotVisualizerModal({ plot, open, onOpenChange, onBookVisit, defaultTab = 'standard' }) {
  const { lang } = useLanguage();
  const t = translations[lang].visualizer;
  const [tab, setTab] = useState(defaultTab);
  const [villaType, setVillaType] = useState('2bhk');
  const [shareUrl, setShareUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  // Sync tab when defaultTab or plot changes
  useEffect(() => { setTab(defaultTab); }, [defaultTab, plot]);
  // Reset share URL when plot changes
  useEffect(() => { setShareUrl(''); setShareCopied(false); }, [plot]);

  if (!plot) return null;

  const logInteraction = (action) => {
    fetch(`${API}/plot-interactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plot_id: plot.id, action }),
    }).catch(() => {});
  };

  const handleTabChange = (newTab) => {
    setTab(newTab);
    if (newTab === 'imagine') logInteraction(`visualize_${villaType}`);
  };

  const handleVillaSwitch = (type) => {
    setVillaType(type);
    logInteraction(`visualize_${type}`);
  };

  const handleBookVisit = () => {
    onOpenChange(false);
    onBookVisit?.(plot.id);
  };

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const res = await fetch(`${API}/share-links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plot_id: plot.id, villa_type: villaType }),
      });
      const { token } = await res.json();
      const url = `${SITE_URL}?share=${token}`;
      setShareUrl(url);
      logInteraction('share');
    } catch { }
    finally { setShareLoading(false); }
  };

  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const shareWhatsApp = () => {
    const msg = lang === 'en'
      ? `Check out Plot #${plot.id} (${plot.area.toLocaleString()} sq.ft) at Lakshmi Nivasa, Red Hills! See the 3D villa preview: ${shareUrl}`
      : `லக்ஷ்மி நிவாசா, ரெட்ஹில்ஸில் பிளாட் #${plot.id} (${plot.area.toLocaleString()} ச.அ.) பாருங்கள்! 3D வில்லா முன்னோட்டம்: ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const approxDim = Math.round(Math.sqrt(plot.area));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="plot-visualizer-modal"
        className="sm:max-w-2xl bg-[#FDFBF7] border-[#E5DFD3] rounded-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        {/* Close button + Tabs */}
        <div className="sticky top-0 z-10 bg-[#FDFBF7] border-b border-[#E5DFD3] px-6 pt-5 pb-0">
          {/* Explicit close button for mobile */}
          <button
            onClick={() => onOpenChange(false)}
            data-testid="visualizer-close-btn"
            className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-[#F4F1EA] hover:bg-[#E5DFD3] flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[#5C6A61]" />
          </button>
          <DialogHeader className="mb-4 pr-8">
            <DialogTitle className="font-serif text-xl text-[#1A2520] flex items-center gap-2">
              <span className="text-[#D48C57]">#{plot.id}</span>
              {t.plotTitle} — {plot.area.toLocaleString()} {t.sqft}
            </DialogTitle>
            <DialogDescription className="text-[#5C6A61] text-sm">{t.modalSubtitle}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-1 border-b border-[#E5DFD3] -mx-6 px-6">
            <button
              onClick={() => handleTabChange('standard')}
              data-testid="tab-standard"
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                tab === 'standard' ? 'text-[#2C4C3B]' : 'text-[#5C6A61] hover:text-[#1A2520]'
              }`}
            >
              <Ruler className="w-3.5 h-3.5 inline mr-1.5" />
              {t.tabStandard}
              {tab === 'standard' && <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2C4C3B]" />}
            </button>
            <button
              onClick={() => handleTabChange('imagine')}
              data-testid="tab-imagine"
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                tab === 'imagine' ? 'text-[#D48C57]' : 'text-[#5C6A61] hover:text-[#1A2520]'
              }`}
            >
              <Eye className="w-3.5 h-3.5 inline mr-1.5" />
              {t.tabImagine}
              {tab === 'imagine' && <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D48C57]" />}
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          {tab === 'standard' ? (
            /* TAB A: Standard View */
            <div className="space-y-4" data-testid="tab-standard-content">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F4F1EA] rounded-xl p-4 border border-[#E5DFD3]">
                  <Ruler className="w-4 h-4 text-[#D48C57] mb-2" />
                  <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.area}</p>
                  <p className="font-serif text-lg font-bold text-[#1A2520]">{plot.area.toLocaleString()} {t.sqft}</p>
                </div>
                <div className="bg-[#F4F1EA] rounded-xl p-4 border border-[#E5DFD3]">
                  <Compass className="w-4 h-4 text-[#2C4C3B] mb-2" />
                  <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.facing}</p>
                  <p className="font-serif text-lg font-bold text-[#1A2520]">{facingMap[lang]?.[plot.facing] || plot.facing}</p>
                </div>
                <div className="bg-[#F4F1EA] rounded-xl p-4 border border-[#E5DFD3]">
                  <MapPin className="w-4 h-4 text-[#5C6A61] mb-2" />
                  <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.approxDimensions}</p>
                  <p className="font-serif text-lg font-bold text-[#1A2520]">~{approxDim}' x {approxDim}'</p>
                </div>
                <div className="bg-[#F4F1EA] rounded-xl p-4 border border-[#E5DFD3]">
                  <Home className="w-4 h-4 text-[#D48C57] mb-2" />
                  <p className="text-[10px] text-[#5C6A61] uppercase tracking-wider">{t.priceRange}</p>
                  <p className="font-serif text-base font-bold text-[#2C4C3B]">
                    &#8377;{(plot.area * 3000).toLocaleString('en-IN')} — &#8377;{(plot.area * 3500).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
              {plot.vaastu && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <Compass className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-amber-800 text-sm font-semibold">{t.vaastuBadge}</p>
                    <p className="text-amber-600 text-xs">{t.vaastuDesc}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* TAB B: Imagine Your Plot */
            <div className="space-y-5" data-testid="tab-imagine-content">
              {/* Community render views */}
              <VillaRenderView area={plot.area} plotId={plot.id} />

              {/* Dynamic hook text */}
              <p className="text-center text-[#1A2520] text-sm font-medium leading-relaxed">
                {lang === 'en'
                  ? `This is how a luxury modern home looks on Plot #${plot.id}.`
                  : `\u0BAA\u0BBF\u0BB3\u0BBE\u0B9F\u0BCD \u0B8E\u0BA3\u0BCD #${plot.id}-\u0B87\u0BB2\u0BCD \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B9A\u0BCA\u0B95\u0BC1\u0B9A\u0BC1 \u0B87\u0BB2\u0BCD\u0BB2\u0BAE\u0BCD \u0B87\u0BAA\u0BCD\u0BAA\u0B9F\u0BBF\u0BA4\u0BCD\u0BA4\u0BBE\u0BA9\u0BCD \u0B85\u0BAE\u0BC8\u0BAF\u0BC1\u0BAE\u0BCD.`
                }
              </p>
              <p className="text-center text-[#5C6A61] text-xs">
                {plot.area.toLocaleString()} {t.sqft} | {facingMap[lang]?.[plot.facing] || plot.facing}
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-5 pt-4 border-t border-[#E5DFD3]">
            <p className="text-[#5C6A61] text-xs mb-3 text-center">
              {lang === 'en'
                ? `You are visualizing Plot #${plot.id}. Enter your number to block a site visit.`
                : `\u0BA8\u0BC0\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0BAA\u0BBF\u0BB3\u0BBE\u0B9F\u0BCD \u0B8E\u0BA3\u0BCD #${plot.id}-\u0B90\u0BAA\u0BCD \u0BAA\u0BBE\u0BB0\u0BCD\u0B95\u0BCD\u0B95\u0BBF\u0BB1\u0BC0\u0BB0\u0BCD\u0B95\u0BB3\u0BCD. \u0B87\u0BA8\u0BCD\u0BA4 \u0B87\u0B9F\u0BA4\u0BCD\u0BA4\u0BC8 \u0BA8\u0BC7\u0BB0\u0BBF\u0BB2\u0BCD \u0BAA\u0BBE\u0BB0\u0BCD\u0BB5\u0BC8\u0BAF\u0BBF\u0B9F \u0B89\u0B99\u0BCD\u0B95\u0BB3\u0BCD \u0B8E\u0BA3\u0BCD\u0BA3\u0BC8\u0BAA\u0BCD \u0BAA\u0BA4\u0BBF\u0BB5\u0BC1 \u0B9A\u0BC6\u0BAF\u0BCD\u0BAF\u0BB5\u0BC1\u0BAE\u0BCD.`
              }
            </p>
            <button
              onClick={handleBookVisit}
              data-testid="visualizer-book-visit"
              className="w-full bg-[#D48C57] hover:bg-[#C07A4A] text-white py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-lg shadow-[#D48C57]/20"
            >
              {t.bookVisitCta}
            </button>

            {/* Share This Plot */}
            <div className="mt-3">
              {!shareUrl ? (
                <button
                  onClick={handleShare}
                  disabled={shareLoading}
                  data-testid="share-plot-btn"
                  className="w-full flex items-center justify-center gap-2 bg-[#F4F1EA] hover:bg-[#E5DFD3] text-[#2C4C3B] py-3 rounded-xl text-sm font-medium transition-all duration-200 border border-[#E5DFD3]"
                >
                  <Share2 className="w-4 h-4" />
                  {shareLoading
                    ? (lang === 'en' ? 'Generating link...' : 'இணைப்பு உருவாகிறது...')
                    : (lang === 'en' ? 'Share This Plot with Family' : 'இந்த பிளாட்டை குடும்பத்துடன் பகிரவும்')
                  }
                </button>
              ) : (
                <div className="space-y-2" data-testid="share-plot-panel">
                  {/* Copy link */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-[#F4F1EA] rounded-lg px-3 py-2 text-xs text-[#5C6A61] truncate border border-[#E5DFD3] font-mono">
                      {shareUrl}
                    </div>
                    <button
                      onClick={copyShareUrl}
                      data-testid="copy-share-link"
                      className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#2C4C3B] text-white flex items-center justify-center hover:bg-[#213A2D] transition-colors"
                    >
                      {shareCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* WhatsApp share */}
                  <button
                    onClick={shareWhatsApp}
                    data-testid="share-whatsapp-btn"
                    className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20BD5B] text-white py-3 rounded-xl text-sm font-semibold transition-all duration-200"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {lang === 'en' ? 'Share via WhatsApp' : 'WhatsApp வழியாக பகிரவும்'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
